import React, { useState, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Modal,
  Button,
  Divider,
  Loader,
  Header,
  Actions,
} from '@gasbuddy/react-components';
import classnames from 'classnames/bind';
import StationInfoBox from '../StationInfoBox';
import EditMembernameModal, { USERNAME_STATE } from '../EditMembernameModal';
import { localyticsEvent, noop } from '../../../lib/utils';
import ANALYTICS_EVENTS from '../../constants/analyticsEvents';
import { RATING_EXPERIENCE } from '../../constants/reviewRatings';
import ThankYou from '../ThankYou';
import ReviewQuestionItem from '../ReviewQuestionItem';
import WriteReview from './WriteReview';
import styles from './SubmitReview.module.css';

const cx = classnames.bind(styles);

export default function SubmitReview({
  stationId,
  isFetchingCategories,
  isSubmittingReview,
  reviewSubmitted,
  submitError,
  error,
  hasDisabledReviews,
  isDisabledMessage,
  ratingCategories,
  fetchReviewSettings,
  submitReview,
  isLoggedIn,
  openModalLinkText,
  notLoggedInLinkText,
  onMembernameUpdateFinish,
  canSetMembername,
  memberName,
}) {
  const [showMembernameCustomization, setShowMembernameCustomization] = useState(false);
  const [shouldShowModal, setShouldShowModal] = useState(false);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [recordedRatings, setRecordedRatings] = useState([]);
  const [reviewText, setReviewText] = useState('');
  const [isRecaptchaVerified, setIsRecaptchaVerified] = useState(false);
  const [recaptchaResponse, setRecaptchaResponse] = useState();
  const isFetching = isFetchingCategories?.[stationId];

  const onShowModal = useCallback(() => {
    setShouldShowModal(true);
    localyticsEvent(ANALYTICS_EVENTS.REVIEW_OPENED, { stationId, variant: 'review' });
  }, [stationId]);

  const onCloseModal = useCallback(() => {
    setShouldShowModal(false);
    setCurrentQuestionIndex(0);
    setShowMembernameCustomization(false);
    setRecordedRatings([]);

    if (reviewSubmitted) {
      fetchReviewSettings(stationId);
    }
  }, [reviewSubmitted, stationId, fetchReviewSettings]);

  const onReviewChange = useCallback(({ target }) => {
    setReviewText(target.value);
  }, []);

  const onReviewSubmit = useCallback((e) => {
    e.preventDefault();

    // exclude rating categories for which user selected NOT SURE option
    const ratedCategories = recordedRatings.filter(r => r.value !== RATING_EXPERIENCE.NOT_SURE);

    submitReview({
      stationId,
      body: reviewText,
      replyRequested: false,
      ratings: recordedRatings,
      'g-recaptcha-response': recaptchaResponse,
    });

    localyticsEvent(ANALYTICS_EVENTS.REVIEW_SUBMITTED, {
      stationId,
      variant: 'review',
      rating_categories_rated: ratedCategories.length > 0,
    });
  }, [stationId, submitReview, reviewText, recordedRatings, recaptchaResponse]);

  const onEditUsername = useCallback(() => {
    localyticsEvent(ANALYTICS_EVENTS.CHANGE_USERNAME_OPENED, { Context: 'Review_Submission' });
    setShowMembernameCustomization(true);
  }, []);

  const handleMembernameUpdateFinish = useCallback((e) => {
    localyticsEvent(ANALYTICS_EVENTS.USERNAME_UPDATED, { Context: 'Review_Submission' });
    onMembernameUpdateFinish(e);
  }, [onMembernameUpdateFinish]);

  const handleRecaptchaVerified = useCallback((response) => {
    setRecaptchaResponse(response);
    setIsRecaptchaVerified(true);
  }, []);

  const handleRecaptchaExpired = useCallback(() => {
    setRecaptchaResponse(null);
    setIsRecaptchaVerified(false);
  }, []);

  const handleReviewCategoryRated = useCallback((categoryId, value) => {
    if (categoryId <= 0 || value < 0 || value > 3) {
      throw new Error(`Unexpected rating category or value: categoryId=${categoryId}, value=${value}`);
    }

    const nextQuestionIndex = currentQuestionIndex + 1;
    const updatedRatings = [].concat(recordedRatings, {
      categoryId,
      stationId,
      value,
    });
    setRecordedRatings(updatedRatings);
    setCurrentQuestionIndex(nextQuestionIndex);
  }, [stationId, recordedRatings, currentQuestionIndex]);

  // Fetch review settings if they are unavailable
  useEffect(() => {
    const hasNotFetched = typeof isFetching === 'undefined';
    if (hasNotFetched && isLoggedIn && !ratingCategories.length && !error) {
      fetchReviewSettings(stationId);
    }
  }, [isFetching, isLoggedIn, ratingCategories, error, fetchReviewSettings, stationId]);

  if (!isLoggedIn) {
    return (
      <Button
        primary
        as="a"
        href="/login"
        fluid
        size="lg"
      >
        {notLoggedInLinkText}
      </Button>
    );
  }

  const shouldShowMessage = hasDisabledReviews || reviewSubmitted;
  const reviewIsSubmittable = reviewText && isRecaptchaVerified;
  const hideHeaderAndButtons = (!isFetching && !ratingCategories.length);

  const shouldShowSuccess = !isDisabledMessage?.length && typeof reviewSubmitted !== 'undefined' && !submitError?.length;
  const messageContent = shouldShowSuccess
    ? (
      <ThankYou
        messageType="review"
        onDone={onCloseModal}
        onEditUserName={onEditUsername}
        isGeneratedUsername={canSetMembername}
        username={memberName}
      />
    )
    : (
      <div className={cx('successContainer')}>
        <span className={cx('successSubHeader')}>
          {isDisabledMessage?.length
            ? isDisabledMessage
            : 'An error occurred while loading the review questions. Please try again later.'
          }
        </span>
      </div>
    );

  let modalContent = <Loader />;
  if (ratingCategories.length) {
    if (currentQuestionIndex >= ratingCategories.length) {
      modalContent = (
        <WriteReview
          submitError={submitError}
          onRecaptchaVerified={handleRecaptchaVerified}
          onRecaptchaExpired={handleRecaptchaExpired}
          onReviewChange={onReviewChange}
          renderRecaptcha={shouldShowModal}
        />
      );
    } else if (ratingCategories[currentQuestionIndex]) {
      modalContent = (
        <ReviewQuestionItem
          item={ratingCategories[currentQuestionIndex]}
          onScoreChosen={handleReviewCategoryRated}
        />
      );
    }
  }

  return (
    <React.Fragment>
      {!error && (
        <Button
          primary
          fluid
          onClick={onShowModal}
          size="lg"
        >
          {openModalLinkText}
        </Button>
      )}
      {showMembernameCustomization && canSetMembername ? (
        <EditMembernameModal
          showModal={shouldShowModal}
          onCloseModal={onCloseModal}
          onFinish={handleMembernameUpdateFinish}
          statusVariant={canSetMembername ? USERNAME_STATE.EDITABLE : USERNAME_STATE.CUSTOMIZED}
          memberName={memberName}
        />
      ) : (
        <Modal
          forceIsShowing={shouldShowModal}
          onClose={onCloseModal}
          size="sm"
          bgClickable={false}
          content={() => (
            <div className={cx('modalBody')}>
              {shouldShowMessage
                ? messageContent
                : (
                  <React.Fragment>
                    {!hideHeaderAndButtons && (
                      <React.Fragment>
                        <Header as="h3">Rate and review this station</Header>
                        <StationInfoBox
                          stationId={stationId}
                          noContainer
                          noRating
                          basic
                        />
                        <Divider />
                      </React.Fragment>
                    )}
                    {modalContent}
                    {!hideHeaderAndButtons && (
                      <React.Fragment>
                        <br />
                        <Divider />
                        <br />
                        {shouldShowMessage
                          ? (
                            <Button
                              primary
                              onClick={onCloseModal}
                              className={cx('closeReviewModalButton')}
                            >
                              Close
                            </Button>
                          )
                          : (
                            <Actions>
                              <Actions.Button
                                primary
                                fluid
                                loading={isSubmittingReview}
                                disabled={!reviewIsSubmittable}
                                onClick={onReviewSubmit}
                                tablet={6}
                                desktop={6}
                              >
                                Submit Station Ratings
                              </Actions.Button>
                              <Actions.Button
                                secondary
                                fluid
                                onClick={onCloseModal}
                                tablet={4}
                                desktop={4}
                              >
                                Cancel
                              </Actions.Button>
                            </Actions>
                          )
                        }
                      </React.Fragment>
                    )}
                  </React.Fragment>
                )}
            </div>
          )}
        />
      )}
    </React.Fragment>
  );
}

SubmitReview.propTypes = {
  stationId: PropTypes.string.isRequired,
  isFetchingCategories: PropTypes.shape({
    [PropTypes.number]: PropTypes.bool,
  }),
  isSubmittingReview: PropTypes.bool,
  reviewSubmitted: PropTypes.bool,
  submitError: PropTypes.string,
  error: PropTypes.bool,
  hasDisabledReviews: PropTypes.bool,
  isDisabledMessage: PropTypes.string,
  ratingCategories: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired,
      question: PropTypes.string.isRequired,
      imageUrl: PropTypes.string.isRequired,
    }).isRequired,
  ),
  fetchReviewSettings: PropTypes.func,
  submitReview: PropTypes.func,
  isLoggedIn: PropTypes.bool,
  openModalLinkText: PropTypes.string,
  notLoggedInLinkText: PropTypes.string,
  onMembernameUpdateFinish: PropTypes.func,
  canSetMembername: PropTypes.bool,
  memberName: PropTypes.string,
};

SubmitReview.defaultProps = {
  isFetchingCategories: undefined,
  isSubmittingReview: false,
  reviewSubmitted: false,
  submitError: '',
  error: false,
  hasDisabledReviews: false,
  isDisabledMessage: '',
  openModalLinkText: 'Write a Review',
  notLoggedInLinkText: 'Log In to Write a Review',
  onMembernameUpdateFinish: noop,
  canSetMembername: false,
  memberName: '',
  ratingCategories: [],
  fetchReviewSettings: noop,
  submitReview: noop,
  isLoggedIn: false,
};
